import { useCateringReport } from '../hooks/useCatering'
import { formatEventDates } from '../lib/dates'
import { formatGBP } from '../lib/money'
import { PrintShell, Section, Table, Empty, usePrintOnReady } from '../components/print/printKit'
import type { EventCatering } from '../types'

// CateringReport (Feature 3) — the global all-events catering PDF. Route
// /catering-report. One section per event with catering, plus an "Outstanding
// items" roll-up of everything still "to confirm". Browser-print, shell-less.

function cost(c: EventCatering): string {
  if (c.costPence != null) return formatGBP(c.costPence)
  return c.costNote ?? 'TBC'
}

function retail(c: EventCatering): string {
  return c.retailPricePence != null ? formatGBP(c.retailPricePence) : '—'
}

const STATUS_LABEL: Record<string, string> = { confirmed: 'Confirmed', tbc: 'To confirm', proposed: 'Proposed' }

export function CateringReport() {
  const { data } = useCateringReport()
  usePrintOnReady(data != null)

  const events = data?.events ?? []
  const outstanding = events.flatMap((e) =>
    e.items.filter((i) => i.status !== 'confirmed').map((i) => ({ eventName: e.eventName, offer: i.offer, status: i.status }))
  )

  return (
    <PrintShell>
      <section style={{ paddingBottom: 12 }}>
        <div style={{ fontSize: 12, color: '#5a5a5a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
          NYMR · Events Catering
        </div>
        <h1 style={{ fontSize: 30 }}>Catering Requirements</h1>
        <p style={{ fontSize: 12, color: '#5a5a5a' }}>
          {events.length} event{events.length === 1 ? '' : 's'} · Generated {new Date().toLocaleString('en-GB')}
        </p>
      </section>

      {events.length === 0 && <Empty>No catering recorded across events.</Empty>}

      {events.map((e) => (
        <Section key={e.eventId} title={e.eventName}>
          <p style={{ fontSize: 12, color: '#5a5a5a', marginTop: -4 }}>{formatEventDates(undefined, e.startDate, e.endDate)}</p>
          <Table headers={['Offer', 'Supplier', 'Cost', 'Retail', 'Status', 'Type']}>
            {e.items.map((i) => (
              <tr key={i.id}>
                <td>
                  <strong>{i.offer}</strong>
                  {i.notes && <div style={{ fontSize: 10.5, color: '#5a5a5a', marginTop: 2 }}>{i.notes}</div>}
                </td>
                <td>{i.supplier ?? '—'}</td>
                <td>{cost(i)}</td>
                <td>{retail(i)}</td>
                <td>{STATUS_LABEL[i.status] ?? i.status}</td>
                <td>{i.isUpsell ? 'Upsell' : 'Included'}</td>
              </tr>
            ))}
          </Table>
        </Section>
      ))}

      {outstanding.length > 0 && (
        <Section title="Outstanding items">
          <Table headers={['Event', 'Offer', 'Status']}>
            {outstanding.map((o, i) => (
              <tr key={i}>
                <td>{o.eventName}</td>
                <td>{o.offer}</td>
                <td>{STATUS_LABEL[o.status] ?? o.status}</td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
    </PrintShell>
  )
}
